/**
 * Config Selection Dialog
 * Reusable dialog for selecting a unit configuration.
 * Used by both single-item and multi-item product flows.
 */

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, TextField,
  InputAdornment, IconButton, Table, TableHead, TableBody, TableRow,
  TableCell, Button, alpha, useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';

export default function ConfigSelectionDialog({
  open,
  onClose,
  configs,
  search,
  onSearchChange,
  onSelect,
  currentConfigId
}) {
  const theme = useTheme();

  const filtered = configs.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '50vh',
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        pb: 2, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
      }}>
        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
          Select Unit Configuration
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflowX: 'hidden' }}>
        <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
          <TextField
            fullWidth
            placeholder="Search configurations..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.primary.main }} />
                </InputAdornment>
              )
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '1rem' } }}
          />
        </Box>
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Configuration</TableCell>
              <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((cfg) => {
              const isSelected = currentConfigId === cfg.id;
              return (
                <TableRow
                  key={cfg.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'grey.50',
                      transform: 'translateX(4px)',
                      boxShadow: 'inset 4px 0 0 black'
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                      py: 2, pl: 3, pr: 3
                    }
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                      {cfg.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cfg.enable_cases ? `${cfg.units_per_case} units/case` : 'Units only'}
                      {cfg.enable_pallets && cfg.enable_cases && `, ${cfg.cases_per_pallet} cases/pallet`}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onSelect(cfg)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        px: 2, py: 1,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        bgcolor: isSelected ? 'black' : 'transparent',
                        color: isSelected ? 'white' : 'inherit',
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          bgcolor: isSelected ? 'black' : 'transparent',
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}
                    >
                      {isSelected ? 'Selected' : 'Select'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {search ? 'No configurations found.' : 'No configurations available.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}
